import _ from 'lodash';
import { consoleStart } from '../../../App';
import { _appUrlPaths, getAppConfigSettings, getFbConfig } from '../../../project/appConfiguration';
import { mobileDeviceTypes, parentStyleOptions, smartPhoneTypes } from '../../devices';
import { gEnums } from '../../enums/globalEnums';
import { timeStampsHelpers } from '../../firestoreData/updates/subUpdates/fsTimeStamps';
import { ammendAppSettings, getAppSettingsFromDb, getHCEAppSettings_docOnly } from '../../viewSettings/actions/getSettings';
import { openExternal } from '../../viewSettings/helpers/settingsLinks';
import { _settingSourceTypes } from '../contexts/ServiceWorkerContext';
import { grts, responseHandlers } from './reducerHelpers/dispatchProps';
import { _showAppLogs } from '../contexts/LogContext';

const _useSettingsDelay = false
const _logCacheSettings = false

const triggerUrlTypes = {
  auth: 'auth',
  cloudArtifactsConsole: 'cloudArtifactsConsole',
  cloudDataConsole: 'cloudDataConsole',
  cloudStorageConsole: 'cloudStorageConsole',
  credentials: 'credentials',
  database: 'database',
  databaseFunctions: 'databaseFunctions',
  databaseSettings: 'databaseSettings',
  databaseStorage: 'databaseStorage',
  development: 'development',
  emailConfirmation: 'emailConfirmation',
  googleAnalytics: 'googleAnalytics',
  production: 'production',
  realtimeDatabase: 'realtimeDatabase',
  serviceAccount: 'serviceAccount',
  serviceApiAccount: 'serviceApiAccount',
  signInDemo: 'signInDemo',
}

const rts = {
  handleAmmend_appHistory: 'handleAmmend_appHistory',
  handleAmmend_appSettings: 'handleAmmend_appSettings',
  handleAmmend_appSettingsCalls: 'handleAmmend_appSettingsCalls',
  handleAmmend_modifiedAppSettings: 'handleAmmend_modifiedAppSettings',
  handleAmmend_regInfo: 'handleAmmend_regInfo',
  handleCommitSettingsOn: 'handleCommitSettingsOn',
  handleCopySettings: 'handleCopySettings',
  handleDatabaseRefresh: 'handleDatabaseRefresh',
  handleDataSourceType: 'handleDataSourceType',
  handleDirectLinksOverride: 'handleDirectLinksOverride',
  handleError_app: 'handleError_app',
  handleGet_eventTimeStamps: 'handleGet_eventTimeStamps',
  handlePropOverride: 'handlePropOverride',
  handleSandboxOn: 'handleSandboxOn',
  handleSet_eventTimeStamps: 'handleSet_eventTimeStamps',
  handleSet_hceSettings: 'handleSet_hceSettings',
  handleSet_startFetch: 'handleSet_startFetch',
  handleSet_endFetch: 'handleSet_endFetch',
  handleSetAppChange: 'handleSetAppChange',
  handleSetDevice: 'handleSetDevice',
  handleSettingsClose: 'handleSettingsClose',
  handleSettingsOff: 'handleSettingsOff',
  handleShow_appDashboard: 'handleShow_appDashboard',
  handleShow_homeScreenPrompt: 'handleShow_homeScreenPrompt',
  handleShow_pageViewer: 'handleShow_pageViewer',
  handleShowHomeScreenHelp: 'handleShowHomeScreenHelp',
  handleHideSplash: 'handleHideSplash',
  handleThemeSelect: 'handleThemeSelect',
  handleToggle_altDatabaseOn: 'handleToggle_altDatabaseOn',
  handleToggle_authOn: 'handleToggle_authOn',
  handleToggle_dataOverrideOn: 'handleToggle_dataOverrideOn',
  handleToggle_dbOrigin: 'handleToggle_dbOrigin',
  handleToggle_demoOverrideOn: 'handleToggle_demoOverrideOn',
  handleToggle_device: 'handleToggle_device',
  handleToggle_globalPreviewOn: 'handleToggle_globalPreviewOn',
  handleToggle_helpOn: 'handleToggle_helpOn',
  handleToggle_highlightThemesOn: 'handleToggle_highlightThemesOn',
  handleToggle_pagePreviewOn: 'handleToggle_pagePreviewOn',
  handleToggle_prodOverrideOn: 'handleToggle_prodOverrideOn',
  handleToggle_settingsLeftOn: 'handleToggle_settingsLeftOn',
  handleToggle_settingsRightOn: 'handleToggle_settingsRightOn',
  handleToggle_sortingOn: 'handleToggle_sortingOn',
  handleUpdateAppReady: 'handleUpdateAppReady',
  handleUpdateAppSettingsBlank: 'handleUpdateAppSettingsBlank',
  handleUpdateHomeSettings: 'handleUpdateHomeSettings',
  updateSettingsSuccess: 'updateSettingsSuccess',
  ...grts
}

export const appSettingsInitialState = (props) => {

  const useCurrentDevice = false

  const { currentEnvironment, hashPath, allowSettingsListen, authUid, database_fns, cache_settings } = props ?? {}
  const appConfigSettings = getAppConfigSettings()

  const { platform } = currentEnvironment ?? {}
  const { currentDevice } = platform ?? {}

  const deviceName = useCurrentDevice ? currentDevice.name : smartPhoneTypes.smartphone

  const currentAppDevice = getDevice(mobileDeviceTypes(), deviceName)

  return {
    allAppSettings: null,
    allowSettingsListen,
    altDatabaseOn: false,
    appConfigSettings,
    appReady: false,
    appSettingsOn: false,
    authUid,
    cache_settings,
    currentAppDevice: currentAppDevice,
    dataRefreshOn: 1,
    dbType: 'dev',
    desktopMode: false,
    helpOn: false,
    highlightThemesOn: false,
    hashPath,
    parentStyleOptions: parentStyleOptions(),
    sandboxOn: false,
    settingsPreviewOn: false,
    showSplash: true,
    showStartSplash: true,
    sp: null,
    updateProps: null,
    view: null,
    database_fns,
    appHistory: {}
  }
};

export const appSettingsReducer = (state, action) => {

  const { appSettings_fns, database_fns, appUser } = state
  const { type, settings, pathViews, pathname, dispatch } = action
  const allAppSettings = Object.assign({}, state.allAppSettings)

  const { handleSet_eventTimeStamps, handleSet_hceDocSetting, handleAmmend_appSettings, updateSuccess } = appSettingsHandlers(dispatch)

  _showAppLogs.reducerTypes && console.log('✔️ appSettingsReducer', type)

  switch (type) {

    case rts.handleAmmend_appSettingsCalls:
      const _settingsCalls = state.settingsCalls ? state.settingsCalls : {}
      _settingsCalls[action.settingsKey] = action.settingsFunction
      return { ...state, settingsCalls: _settingsCalls }

    case rts.handleAmmend_regInfo:
      return { ...state, regInfo: action.regInfo }

    case rts.handleAmmend_appHistory:
      const _hist = state.appHistory ? state.appHistory : {}
      _hist[action.appName] = action.appPath
      return { ...state, appHistory: _hist }

    case rts.handleSet_startFetch:
      return { ...state, fetchingSettings: true }

    case rts.handleSet_endFetch:
      return { ...state, fetchingSettings: false }

    case rts.handleSet_hceSettings:
      const app_settings = state?.appSettings ? _.cloneDeep(state.appSettings) : {}
      app_settings.hceSettings = action.combinedSettings
      return { ...state, appSettings: app_settings }

    // this updates the appSettings for the current settingsDocName (event/client/home)
    case rts.handleAmmend_appSettings:

      // settings from action 
      const _dsks = action.settingsDocKeys ? action.settingsDocKeys : state.settingsDocKeys

      const { settingsSourceType: _settingsSourceType } = action

      let fbErrorCode;

      if (_logCacheSettings) {
        console.log('handleAmmend_appSettings', settings)
      }

      if (settings.error) {
        console.log('error', settings.error)
        switch (settings.error.code) {
          case 'resource-exhausted':
            fbErrorCode = settings.error.code
            console.log('fbErrorCode', fbErrorCode)
            break;
          default:
          // nothing 
        }
      }

      const { global } = settings ?? {}
      const { projectOverview, dataOptions, projectOptions } = global ?? {}
      const { projectType } = projectOverview ?? {}

      ammendProjctOptions(global)
      const { _availableAddTypes, _availableSubActionTypes } = getSubActionTypes(projectType, projectOptions, dataOptions)

      if (_availableAddTypes) { dataOptions.availableAddTypes = _availableAddTypes }
      if (_availableSubActionTypes) { dataOptions.availableSubActionTypes = _availableSubActionTypes }

      allAppSettings[settings.settingsDocName] = {
        global: settings.global,
        viewItems: settings.viewItems,
        views: settings.views,
        settingsDocKey: settings.settingsDocKey,
        settingsDocName: settings.settingsDocName,
        isGlobalStatic: settings.isGlobalStatic,
        isViewStatic: settings.isViewStatic,
        status: settings.status,
        time_stamp: settings.time_stamp,
        time_stamp_date: settings.time_stamp ? new Date(settings.time_stamp) : null,
        missingSettings: settings.missingSettings,
        error: settings.error,
        currentPathName: pathname,
        settingsSourceType: _settingsSourceType ? _settingsSourceType : settings.settingsSourceType,
      }

      const _appSettings = checkSettings(allAppSettings, _dsks)

      const { hceSettings } = _appSettings ?? {}
      const { home } = hceSettings ?? {}
      const { global: global_home } = home ?? {}
      const { appLanding: appLanding_global, appShortUrls: appShortUrls_global } = global_home ?? {}

      console.log('appShortUrls_global', appShortUrls_global)

      let _currentAppSettings;
      let _modifiedAppSettings;

      if (state.appSettings && _appSettings) {
        const { settingsChanged } = checkEqual_settings(state, _appSettings)
        if (settingsChanged && _useSettingsDelay) {
          _modifiedAppSettings = _appSettings
          _currentAppSettings = state.appSettings
          return {
            ...state,
            currentPathName: pathname,
            modifiedAppSettings: _modifiedAppSettings,
            appSettings: _currentAppSettings,
          }
        } else {
          _currentAppSettings = _appSettings
        }
      } else {
        _currentAppSettings = _appSettings
      }

      if (_logCacheSettings) {
        console.log('allAppSettings', allAppSettings)
      }

      return {
        ...state,
        currentPathName: pathname,
        settingsDocKeys: _dsks,
        allAppSettings: allAppSettings,
        appSettings: _currentAppSettings,
        settingsSourceType: _settingsSourceType ? _settingsSourceType : settings.settingsSourceType,
        appUpdated: false,
        appReady: true,
        showSplash: true,
        appLanding_global,
        appShortUrls_global,
        fbErrorCode,
      };

    case rts.handleError_app:
      return { ...state, appError: action.appError }

    case rts.handleToggle_dbOrigin:
      return { ...state, dbOrigin: action.dbOrigin }

    case rts.handleToggle_device:
      return { ...state, toggleDeviceOn: !state.toggleDeviceOn }

    case rts.handleShow_appDashboard:
      return { ...state, showAppDashboard: !state.showAppDashboard, selectedDataMode: action.selectedDataMode }

    case rts.handleCopySettings:
      return { ...state, copiedData: action.copiedData }

    case rts.handleUpdateAppSettingsBlank: {
      allAppSettings[action.docName] = {}
      return {
        ...state,
        allAppSettings: allAppSettings,
      };
    }

    case rts.handleAmmend_modifiedAppSettings:
      return {
        ...state,
        appSettings: state.modifiedAppSettings,
        modifiedAppSettings: null,
      }

    case rts.handleGet_eventTimeStamps:
      const allowListen = true
      timeStampsHelpers.getEventTimeStamps_db(database_fns, pathViews, allowListen, handleSet_eventTimeStamps)
      return state

    case rts.handleSet_eventTimeStamps:
      return { ...state, timeStamps_event_db: action.timeStamps_event_db }

    case rts.handleUpdateHomeSettings:
      const { dataItem, dataKey } = action
      const { home: settings_home } = allAppSettings ?? {}
      const { global: home_global } = settings_home ?? {}
      if (home_global && home_global[dataKey]) {
        home_global[dataKey] = dataItem
        const dataToUpdate = {
          [dataKey]: dataItem
        }
        appSettings_fns.updateHomeGlobalSettingsToDb(dataToUpdate, updateSuccess, appUser)
      }
      return { ...state, updating: true }

    case rts.handleUpdateAppReady:
      return {
        ...state,
        appReady: true,
      };

    case rts.handleToggle_sortingOn:
      return {
        ...state,
        sortingOn: !state.sortingOn,
      }

    case rts.handleToggle_helpOn:
      return {
        ...state,
        helpOn: !state.helpOn,
      }

    case rts.handleSettingsOff:
      if (state.appSettingsOn) {
        return {
          ...state,
          appSettingsOn: false,
          settingsPreviewOn: false
        }
      } else {
        return { ...state }
      }

    case rts.handleToggle_settingsLeftOn:
      return {
        ...state,
        settingsLeftOn: !state.settingsLeftOn,
        settingsRightOn: false,
        pullRight: false
      }

    case rts.handleToggle_settingsRightOn:
      return {
        ...state,
        settingsRightOn: !state.settingsRightOn,
        settingsLeftOn: false,
        pullRight: false
      }

    case rts.handleSettingsClose:
      return {
        ...state,
        settingsLeftOn: false,
        settingsRightOn: false,
      }

    case rts.handleSandboxOn:
      return {
        ...state,
        sandboxOn: !state.sandboxOn,
      }

    case rts.handleCommitSettingsOn:
      return {
        ...state,
        commitSettingsOn: !state.commitSettingsOn,
      }

    case rts.handleToggle_authOn:
      return { ...state, authOn: !state.authOn }

    case rts.handleToggle_prodOverrideOn:
      return {
        ...state,
        prodOverrideOn: !state.prodOverrideOn,
      }

    case rts.handleToggle_demoOverrideOn:
      return {
        ...state,
        demoOverrideOn: !state.demoOverrideOn,
      }

    case rts.handleToggle_dataOverrideOn:
      return {
        ...state,
        dataOverrideOn: !state.dataOverrideOn,
      }

    case rts.handleDirectLinksOverride:
      return {
        ...state,
        directLinksOverrideOn: !state.directLinksOverrideOn,
      }

    case rts.handlePropOverride:
      return {
        ...state,
        propOverrideOn: action.overrideOn,
      }

    case rts.handleDatabaseRefresh:
      return {
        ...state,
        dataRefreshOn: state.dataRefreshOn === 1 ? 2 : 1,
      }

    case rts.handleDataSourceType:
      return {
        ...state,
        tempDataSource: action.dataSourceType,
        tempGoogleSheetsId: action.googleSheetId
      }

    case rts.handleToggle_highlightThemesOn:
      return {
        ...state,
        highlightThemesOn: !state.highlightThemesOn,
      }

    case rts.handleToggle_altDatabaseOn:
      return {
        ...state,
        altDatabaseOn: !state.altDatabaseOn,
      }

    case rts.handleThemeSelect:
      return {
        ...state,
        themeSelected: state.themeSelected && state.themeSelected === action.parentSettingKey ? null : action.parentSettingKey,
      }

    case rts.handleToggle_globalPreviewOn:
      return {
        ...state,
        settingsPreviewOn: !state.settingsPreviewOn,
      }

    case rts.handleToggle_pagePreviewOn:
      return {
        ...state,
        settingsPreviewOn: false,
      }

    case rts.handleSetDevice:
      const x = getDevice(mobileDeviceTypes(), action.deviceValue)
      return {
        ...state,
        desktopMode: x && x.key === smartPhoneTypes.desktop ? true : false,
        currentAppDevice: x ? x : state.currentAppDevice,
      }

    case rts.handleHideSplash:
      return {
        ...state,
        showSplash: false
      }

    // case rts.handleStartSplash:
    //   return {
    //     ...state,
    //     showStartSplash: false
    //   }

    case rts.handleSetAppChange:
      return {
        ...state,
        appUpdated: action.updated
      }

    case rts.handleShow_homeScreenPrompt:
      return {
        ...state,
        showHomeScreenPrompt: action.show,
      }

    case rts.handleShowHomeScreenHelp:
      return {
        ...state,
        showHomeScreenHelp: !state.showHomeScreenHelp,
      }

    case rts.handleShow_pageViewer:
      return { ...state, showPageViewer: !state.showPageViewer, selectedViewKey: action.selectedViewKey }

    case rts.updateSettingsSuccess:
      return {
        ...state,
        commitSettingsOn: !state.commitSettingsOn,
      }

    case rts.updateSuccess:
    case rts.updateSuccessAlt:
      return { ...state, updateSuccess: true, showConfirmationQuestion: false }

    case rts.updateError:
      return { ...state, updateSuccess: false, updateError: true }

    default:
      return { ...state };
  }
}

export const appSettingsHandlers = (dispatch) => {

  return {
    handleSet_startFetch: () => { dispatch({ type: rts.handleSet_startFetch, dispatch }) },
    handleSet_endFetch: () => { dispatch({ type: rts.handleSet_endFetch, dispatch }) },
    handleToggle_previewOn: (isGlobal) => {
      if (isGlobal) {
        dispatch({ type: rts.handleToggle_globalPreviewOn })
      } else {
        dispatch({ type: rts.handleToggle_pagePreviewOn })
      }
    },
    handleAmmend_appHistory: (appName, appPath) => { dispatch({ type: rts.handleAmmend_appHistory, dispatch, appName, appPath }) },
    handleAmmend_appSettings: (settings, pathViews, settingsDocKeys, pathname, settingsSourceType) => { dispatch({ type: rts.handleAmmend_appSettings, dispatch, settings, pathViews, settingsDocKeys, pathname, settingsSourceType }) },
    handleAmmend_appSettingsCalls: (settingsKey, settingsFunction) => { dispatch({ type: rts.handleAmmend_appSettingsCalls, dispatch, settingsKey, settingsFunction }) },
    handleAmmend_modifiedAppSettings: () => { dispatch({ type: rts.handleAmmend_modifiedAppSettings, dispatch }) },
    handleAmmend_regInfo: (regInfo) => { dispatch({ type: rts.handleAmmend_regInfo, dispatch, regInfo }) },
    handleCommitSettingsOn: () => { dispatch({ type: rts.handleCommitSettingsOn, dispatch }) },
    handleCopySettings: (copiedData) => { dispatch({ type: rts.handleCopySettings, dispatch, copiedData }) },
    handleDatabaseRefresh: () => { dispatch({ type: rts.handleDatabaseRefresh, dispatch }) },
    handleDataSourceType: (dataSourceType, googleSheetId) => { dispatch({ type: rts.handleDataSourceType, dispatch, dataSourceType, googleSheetId }) },
    handleDirectLinksOverride: () => { dispatch({ type: rts.handleDirectLinksOverride, dispatch }) },
    handleError_app: (appError) => { dispatch({ type: rts.handleError_app, dispatch, appError }) },
    handleGet_eventTimeStamps: (pathViews) => { dispatch({ type: rts.handleGet_eventTimeStamps, dispatch, pathViews }) },
    // handleGet_hceDocSetting: (settingsDocKeys) => { dispatch({ type: rts.handleGet_hceDocSetting, dispatch, settingsDocKeys }) },
    handlePropOverride: (overrideOn) => { dispatch({ type: rts.handlePropOverride, dispatch, overrideOn }) },
    handleSandboxOn: () => { dispatch({ type: rts.handleSandboxOn, dispatch }) },
    handleSet_eventTimeStamps: (timeStamps_event_db) => { dispatch({ type: rts.handleSet_eventTimeStamps, dispatch, timeStamps_event_db }) },
    handleSet_hceSettings: (combinedSettings) => { dispatch({ type: rts.handleSet_hceSettings, dispatch, combinedSettings }) },
    handleSetAppChange: (updated) => { dispatch({ type: rts.handleSetAppChange, dispatch, updated }) },
    handleSetDevice: (deviceValue) => { dispatch({ type: rts.handleSetDevice, dispatch, deviceValue }) },
    handleSettingsClose: () => { dispatch({ type: rts.handleSettingsClose, dispatch }) },
    handleSettingsOff: () => { dispatch({ type: rts.handleSettingsOff, dispatch }) },
    handleShow_appDashboard: (selectedDataMode) => { dispatch({ type: rts.handleShow_appDashboard, dispatch, selectedDataMode }) },
    handleShow_homeScreenPrompt: (show) => { dispatch({ type: rts.handleShow_homeScreenPrompt, dispatch, show }) },
    handleShow_pageViewer: (selectedViewKey) => { dispatch({ type: rts.handleShow_pageViewer, dispatch, selectedViewKey }) },
    handleShowHomeScreenHelp: () => { dispatch({ type: rts.handleShowHomeScreenHelp, dispatch }) },
    handleHideSplash: () => { dispatch({ type: rts.handleHideSplash, dispatch }) },
    handleThemeSelect: (parentSettingKey) => { dispatch({ type: rts.handleThemeSelect, dispatch, parentSettingKey }) },
    handleToggle_altDatabaseOn: () => { dispatch({ type: rts.handleToggle_altDatabaseOn, dispatch }) },
    handleToggle_authOn: () => { dispatch({ type: rts.handleToggle_authOn, dispatch }) },
    handleToggle_dataOverrideOn: () => { dispatch({ type: rts.handleToggle_dataOverrideOn, dispatch }) },
    handleToggle_dbOrigin: (dbOrigin) => { dispatch({ type: rts.handleToggle_dbOrigin, dispatch, dbOrigin }) },
    handleToggle_demoOverrideOn: () => { dispatch({ type: rts.handleToggle_demoOverrideOn, dispatch }) },
    handleToggle_device: () => { dispatch({ type: rts.handleToggle_device }) },
    handleToggle_helpOn: () => { dispatch({ type: rts.handleToggle_helpOn, dispatch }) },
    handleToggle_highlightThemesOn: () => { dispatch({ type: rts.handleToggle_highlightThemesOn, dispatch }) },
    handleToggle_prodOverrideOn: () => { dispatch({ type: rts.handleToggle_prodOverrideOn, dispatch }) },
    handleToggle_settingsLeftOn: () => { dispatch({ type: rts.handleToggle_settingsLeftOn, dispatch }) },
    handleToggle_settingsOn: () => { dispatch({ type: rts.handleToggle_settingsOn, dispatch }) },
    handleToggle_settingsRightOn: () => { dispatch({ type: rts.handleToggle_settingsRightOn, dispatch }) },
    handleToggle_sortingOn: () => { dispatch({ type: rts.handleToggle_sortingOn, dispatch }) },
    handleUpdateAppReady: () => { dispatch({ type: rts.handleUpdateAppReady, dispatch }) },
    handleUpdateAppSettingsBlank: (docName) => { dispatch({ type: rts.handleUpdateAppSettingsBlank, dispatch, docName }) },
    handleUpdateHomeSettings: (dataItem, dataKey) => { dispatch({ type: rts.handleUpdateHomeSettings, dispatch, dataItem, dataKey }) },
    updateSettingsSuccess: () => { dispatch({ type: rts.updateSettingsSuccess, dispatch }) },
    updateSuccess: () => { dispatch({ type: rts.updateSuccess, dispatch }) },
    ...responseHandlers(dispatch)
  }
}

const getDevice = (mobileDeviceTypes, value) => _.find(mobileDeviceTypes, { 'value': value })

const checkSettings = (allAppSettings, settingsDocKeys) => {

  let settingsStatus = {};
  let settingsReady = true;
  let settingsError = null;
  let missingSettings = null

  const _hceSettings = {}

  if (allAppSettings && settingsDocKeys) {
    Object.keys(settingsDocKeys).forEach(sdnKey => {
      const sdn = settingsDocKeys[sdnKey]
      if (allAppSettings[sdn]) {
        settingsStatus[sdnKey] = { dataFetched: true }
        _hceSettings[sdnKey] = allAppSettings[sdn]
      }
    })
  }

  Object.keys(settingsDocKeys).forEach(sdk => {
    // settingsStatus[sdk] = { ready: _hceSettings[sdk] ? true : false }
    if (settingsStatus[sdk]) {
      settingsStatus[sdk].ready = _hceSettings[sdk] ? true : false
    }
    if (!_hceSettings[sdk]) { settingsReady = false }
    if (_hceSettings[sdk] && _hceSettings[sdk].error) { settingsError = _hceSettings[sdk].error }
    if (_hceSettings[sdk] && _hceSettings[sdk].missingSettings) { missingSettings = _hceSettings[sdk].missingSettings }
  })

  if (settingsReady && consoleStart) { console.log('_hceSettings', _hceSettings) }

  return { settingsReady, settingsStatus, hceSettings: _hceSettings, settingsError, missingSettings }

}

const ammendProjctOptions = (global) => {

  const { pageOptions, projectOptions } = global ?? {}

  console.log('pageOptions', pageOptions)

  const { allowProfiles, allowGeoList, allowGeoLocation, allowFeeds, allowImageLinks, allowTwitter, allowVideoLinks, allowMerchandise, allowTickets } = projectOptions ?? {}

  if (allowProfiles || allowGeoList || allowGeoLocation || allowFeeds || allowImageLinks || allowTwitter || allowVideoLinks || allowMerchandise || allowTickets) {
    const _pageOptions = pageOptions ? pageOptions : {}
    if (allowProfiles) { _pageOptions.allowProfiles = allowProfiles }
    if (allowGeoList) { _pageOptions.allowGeoList = allowGeoList }
    if (allowGeoLocation) { _pageOptions.allowGeoLocation = allowGeoLocation }
    if (allowFeeds) { _pageOptions.allowFeeds = allowFeeds }
    if (allowImageLinks) { _pageOptions.allowImageLinks = allowImageLinks }
    if (allowTwitter) { _pageOptions.allowTwitter = allowTwitter }
    if (allowVideoLinks) { _pageOptions.allowVideoLinks = allowVideoLinks }
    if (allowMerchandise) { _pageOptions.allowMerchandise = allowMerchandise }
    if (allowTickets) { _pageOptions.allowTickets = allowTickets }
    global.pageOptions = _pageOptions
  }

}

const getSubActionTypes = (projectType, projectOptions, dataOptions) => {

  const { availableAddTypes: availableAddTypes_x, availableSubActionTypes: availableSubActionTypes_x } = projectOptions ?? {}
  const { availableAddTypes, availableSubActionTypes } = dataOptions ?? {}

  const aats = gEnums.availableAddTypes
  const asats = gEnums.availableSubActionTypes

  switch (projectType) {
    case gEnums.projectTypes.golf:
      return {
        _availableAddTypes: availableAddTypes ? availableAddTypes : [aats.golfMatch, aats.golfTournament],
        _availableSubActionTypes: availableSubActionTypes ? availableSubActionTypes :
          [
            asats.createGolfTournament,
            asats.createRoundMatches,
            asats.importGolfTournament,
            asats.createGolfLineups,
            asats.assignStartHole]
      }

    case gEnums.projectTypes.sports:
      return {
        _availableAddTypes: availableAddTypes ? availableAddTypes : null,
        _availableSubActionTypes: availableSubActionTypes ? availableSubActionTypes :
          [
            // asats.backupSeasons,
            asats.createAlignment,
            asats.createAllStarTeams,
            asats.createHistory,
            asats.createPlayoff,
            asats.createRankings,
            asats.createRoster,
            asats.createSchedule,
            asats.createScheduleDates,
            asats.createSchools,
            asats.createSeason,
            asats.createSportsTeams,
            asats.createStaff,
            asats.createTournament,
            asats.createTournamentPlayoff,
            asats.editSeason,
            asats.editTryoutRoster,
            asats.nominatePlayers,
            asats.submitRankings,
            asats.updateDistricts,
            asats.updateLeagueSports,
            asats.updateMatchRealtimeScore,
            asats.updateMatchScore,
            asats.updateOrganizations,
            asats.updateOrganizationSports,
            asats.updateSchoolSports,
          ]
      }

    default:
      return {
        _availableAddTypes: availableAddTypes,
        _availableSubActionTypes: availableSubActionTypes,
      }
  }

}

const checkEqual_settings = (state, _appSettings) => {

  if (state.appSettings && _appSettings) {

    const { hceSettings: hce_state } = state.appSettings
    const { hceSettings: hce } = _appSettings
    const { event_key: event_state } = hce_state ?? {}
    const { event_key: event } = hce ?? {}
    const { global: global_state, viewItems: viewItems_state, views: views_state } = event_state ?? {}
    const { global, viewItems, views } = event ?? {}

    const equal = {
      global: global_state && global ? _.isEqualWith(global_state, global, customComparer) : true,
      viewItems: viewItems_state && viewItems ? _.isEqualWith(viewItems_state, viewItems, customComparer) : true,
      views: views_state && views ? _.isEqualWith(views_state, views, customComparer) : true,
    };

    const differences = {
      global: !equal.global ? getDifferences(global_state, global) : null,
      viewItems: !equal.viewItems ? getDifferences(viewItems_state, viewItems) : null,
      views: !equal.views ? getDifferences(views_state, views) : null,
    };

    const settingsChanged = !equal.global || !equal.viewItems || !equal.views

    return { settingsChanged, equal, differences }
  }
}

const getDifferences = (obj1, obj2) => {
  const differences = {};

  const keys = _.union(_.keys(obj1), _.keys(obj2));
  keys.forEach(key => {
    if (!_.isEqualWith(obj1[key], obj2[key], customComparer)) {
      differences[key] = { obj1: obj1[key], obj2: obj2[key] };
    }
  });
  return differences;
};

const customComparer = (obj1, obj2) => {
  return _.isEqual(_.sortBy(_.toPairs(obj1)), _.sortBy(_.toPairs(obj2)));
};